import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowDownLeft, ArrowUpRight, Lock, History, Sparkles, Users, RotateCcw,
} from "lucide-react";
import { api, formatApiErrorDetail, formatMXN } from "../lib/api";
import { useScope } from "../context/ScopeContext";
import EmptyState from "../components/EmptyState";
import ScopeToggle from "../components/ScopeToggle";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const MONTH_ES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

const monthLabel = (year, month) => `${MONTH_ES[month - 1] || ""} ${year}`;

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("es-MX", {
      day: "2-digit", month: "short", year: "numeric",
    }).format(new Date(iso));
  } catch {
    return "";
  }
};

export default function Historial() {
  const navigate = useNavigate();
  const { scope, partner, hasHousehold } = useScope();
  const [months, setMonths] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Reopen flow
  const [reopenTarget, setReopenTarget] = useState(null);
  const [reopenReason, setReopenReason] = useState("");
  const [reopenRunning, setReopenRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [closedRes, summary] = await Promise.all([
        api.get("/months/closed", { params: { scope } }),
        api.get("/dashboard/summary", { params: { scope } }),
      ]);
      setMonths(closedRes.data);
      setCurrent(summary.data);
    } catch (_) {
      setMonths([]);
      setCurrent(null);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  // Reopen handlers (kept top-level so MonthCard remains stateless)
  const openReopen = (m) => {
    setReopenTarget(m);
    setReopenReason("");
  };
  const runReopen = async () => {
    if (!reopenTarget) return;
    const reason = reopenReason.trim();
    if (reason.length < 10) {
      toast.error("Cuéntanos el motivo (mínimo 10 caracteres).");
      return;
    }
    setReopenRunning(true);
    try {
      const { data } = await api.post(
        `/months/${reopenTarget.year}/${reopenTarget.month}/reopen`,
        { reason },
        { params: { scope } },
      );
      toast.success(`Mes reabierto · ${data.snapshots_deleted} snapshot${data.snapshots_deleted === 1 ? "" : "s"} eliminados`);
      setReopenTarget(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo reabrir");
      setReopenRunning(false);
      return;
    }
    setReopenRunning(false);
  };

  const isShared = scope === "shared";
  const headlineEyebrow = isShared ? "Historial compartido" : "Historial";
  const headline = isShared ? "Su trayectoria juntos" : "Tu trayectoria";
  const subtitle = isShared
    ? "Cómo evoluciona el dinero común. Cada mes cerrado pasa su saldo final al siguiente."
    : "Cada mes cerrado pasa su saldo final al siguiente. Así se construye tu saldo real.";

  return (
    <div data-testid="historial-screen">
      <header className="pt-10 pb-4 px-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="historial-back"
          className="inline-flex items-center gap-1 text-soft hover:text-ink text-sm"
        >
          <ArrowLeft size={16} /> Volver
        </button>
        <p className="mt-3 text-xs font-bold uppercase tracking-[0.18em] text-sage-700 flex items-center gap-1.5">
          {isShared && <Users size={12} />} {headlineEyebrow}
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">
          {headline}
        </h1>
        <p className="mt-1.5 text-sm text-muted2 leading-relaxed">{subtitle}</p>
      </header>

      <div className="px-6 mb-3">
        <ScopeToggle />
      </div>

      <div className="px-6 mt-2 space-y-3">
        {loading ? (
          <p className="text-soft text-sm text-center py-8">Cargando…</p>
        ) : months.length === 0 ? (
          <EmptyState
            icon={History}
            title={isShared ? "Aún sin meses cerrados juntos" : "Aún sin meses cerrados"}
            description={
              isShared
                ? hasHousehold
                  ? "En cuanto pase el primer mes como pareja, verás aquí el cierre compartido."
                  : "Vincula una pareja para ver el historial compartido."
                : "En cuanto pase el primer mes, verás aquí tu cierre con saldo inicial y final."
            }
            testid="historial-empty"
          />
        ) : (
          <>
            {current?.current_month && (
              <MonthCard
                year={current.current_month.year}
                month={current.current_month.month}
                opening_balance={current.current_month.opening_balance}
                income={current.current_month.income}
                expense={current.current_month.expense}
                closing_balance={current.current_month.closing_balance}
                inProgress
                isShared={isShared}
                testid="historial-current"
              />
            )}

            {months.map((m, idx) => {
              const next = idx === 0 ? current?.current_month : months[idx - 1];
              const flowsInto = next ? monthLabel(next.year, next.month) : null;
              return (
                <React.Fragment key={`${m.year}-${m.month}`}>
                  <CarryOverConnector
                    closing={m.closing_balance}
                    nextLabel={flowsInto}
                    isShared={isShared}
                  />
                  <MonthCard
                    year={m.year}
                    month={m.month}
                    opening_balance={m.opening_balance}
                    income={m.income}
                    expense={m.expense}
                    closing_balance={m.closing_balance}
                    closed_at={m.closed_at}
                    isShared={isShared}
                    onReopen={() => openReopen(m)}
                    testid={`historial-month-${m.year}-${m.month}`}
                  />
                </React.Fragment>
              );
            })}

            <div className="rounded-3xl bg-sage-50 border border-sage-100 p-5 mt-4">
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-xl bg-white text-sage-700 flex items-center justify-center shrink-0">
                  {isShared ? <Users size={16} /> : <Sparkles size={16} />}
                </div>
                <div>
                  <p className="font-display font-bold text-ink">
                    {isShared ? "Su dinero común no se reinicia" : "Tu dinero no se reinicia"}
                  </p>
                  <p className="text-sm text-muted2 leading-relaxed mt-1">
                    {isShared
                      ? "El saldo final de su mes anterior se convierte en el punto de partida del siguiente. Es dinero de los dos, visto en continuidad."
                      : "En FiPer el saldo final de tu mes anterior es tu punto de partida del siguiente — para que veas el dinero real, no el aislado."}
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <AlertDialog
        open={reopenTarget !== null}
        onOpenChange={(o) => { if (!o && !reopenRunning) setReopenTarget(null); }}
      >
        <AlertDialogContent
          className="max-w-sm bg-bone border-line rounded-3xl"
          data-testid="confirm-reopen-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">
              ¿Reabrir {reopenTarget && monthLabel(reopenTarget.year, reopenTarget.month)}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-soft text-sm leading-relaxed">
              Operación delicada: se eliminará el snapshot cerrado de ese mes y los meses posteriores. Los movimientos reales NO se tocan; los saldos se recalculan en cadena. Queda registrada quién, cuándo y por qué.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <label className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700 block">
              Motivo (obligatorio · mínimo 10 caracteres)
            </label>
            <textarea
              data-testid="reopen-reason"
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
              rows={3}
              maxLength={300}
              placeholder="Ej: Olvidé registrar un gasto importante, necesito ajustar el cierre."
              className="w-full bg-white border border-line rounded-xl px-3 py-3 outline-none focus:border-sage-500 text-sm resize-none"
              disabled={reopenRunning}
            />
            <p className="text-[10px] text-soft text-right">
              {reopenReason.trim().length}/300
            </p>
          </div>
          <AlertDialogFooter className="gap-2 sm:gap-2 mt-2">
            <AlertDialogCancel
              disabled={reopenRunning}
              data-testid="reopen-cancel"
              className="bg-white border-line text-ink rounded-2xl font-bold"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); runReopen(); }}
              disabled={reopenRunning || reopenReason.trim().length < 10}
              data-testid="reopen-confirm"
              className="bg-clay text-bone hover:bg-clay/90 rounded-2xl font-bold disabled:opacity-50"
            >
              {reopenRunning ? "Reabriendo…" : "Sí, reabrir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CarryOverConnector({ closing, nextLabel, isShared }) {
  if (!nextLabel) return null;
  return (
    <div className="flex items-center gap-3 px-2 -my-1" aria-hidden="true">
      <div className="w-px h-6 bg-sage-200 ml-9" />
      <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700">
        Saldo {formatMXN(closing)} {isShared ? "pasa juntos a" : "pasa a"} {nextLabel}
      </span>
    </div>
  );
}

function MonthCard({
  year, month, opening_balance, income, expense, closing_balance,
  closed_at, inProgress = false, isShared = false, onReopen, testid,
}) {
  return (
    <div
      data-testid={testid}
      className={`rounded-3xl border shadow-card p-5 ${
        inProgress
          ? "bg-sage-900 text-bone border-sage-900"
          : "bg-white text-ink border-line/70"
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className={`text-[10px] font-bold uppercase tracking-[0.16em] ${inProgress ? "text-bone/70" : "text-sage-700"}`}>
            {inProgress
              ? (isShared ? "Mes en curso · compartido" : "Mes en curso")
              : closed_at ? `Cerrado el ${formatDate(closed_at)}` : "Cerrado"}
          </p>
          <h3 className={`font-display text-2xl font-extrabold tracking-tight capitalize mt-0.5 ${inProgress ? "text-bone" : "text-ink"}`}>
            {monthLabel(year, month)}
          </h3>
        </div>
        <span
          className={`shrink-0 text-[10px] font-bold uppercase tracking-[0.16em] px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${
            inProgress ? "bg-bone/15 text-bone" : "bg-sage-50 text-sage-700"
          }`}
        >
          {inProgress ? "En curso" : <><Lock size={10} /> Cerrado</>}
        </span>
      </div>

      <div className="mt-4 space-y-2.5">
        <Row label={isShared ? "Saldo inicial juntos" : "Saldo inicial"}
          value={formatMXN(opening_balance)} tone={inProgress ? "light" : "soft"} />
        <Row label={isShared ? "Entró al espacio común" : "Ingresos"}
          icon={ArrowDownLeft} value={`+ ${formatMXN(income)}`}
          tone={inProgress ? "light-positive" : "positive"} />
        <Row label={isShared ? "Salió del espacio común" : "Gastos"}
          icon={ArrowUpRight} value={`− ${formatMXN(expense)}`}
          tone={inProgress ? "light" : "neutral"} />
        <Divider inProgress={inProgress} />
        <Row label={isShared ? "Saldo final juntos" : "Saldo final"}
          value={formatMXN(closing_balance)} bold
          tone={inProgress ? "light-bold" : "bold"} />
      </div>

      {!inProgress && onReopen && (
        <button
          type="button"
          onClick={onReopen}
          data-testid={`historial-reopen-${year}-${month}`}
          className="mt-4 w-full inline-flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-soft hover:text-sage-700 py-2 rounded-xl border border-dashed border-line hover:border-sage-200 transition-colors"
        >
          <RotateCcw size={11} /> Reabrir este mes
        </button>
      )}
    </div>
  );
}

function Row({ label, icon: Icon, value, bold, tone = "neutral" }) {
  const labelCls = {
    soft: "text-muted2", light: "text-bone/85", "light-bold": "text-bone",
    "light-positive": "text-bone/95", positive: "text-muted2",
    neutral: "text-muted2", bold: "text-ink font-bold",
  }[tone];
  const valueCls = {
    soft: "text-ink", light: "text-bone",
    "light-bold": "text-bone font-extrabold text-xl",
    "light-positive": "text-bone", positive: "text-sage-700",
    neutral: "text-ink", bold: "text-ink font-extrabold text-xl",
  }[tone];
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={`inline-flex items-center gap-2 text-sm ${labelCls}`}>
        {Icon && <Icon size={13} />}
        {label}
      </span>
      <span className={`font-display tabular ${valueCls} ${bold ? "" : "font-bold"}`}>
        {value}
      </span>
    </div>
  );
}

function Divider({ inProgress }) {
  return <div className={`h-px ${inProgress ? "bg-bone/15" : "bg-line/70"}`} />;
}
